/**
 * Client-side calls to /api/auth/* endpoints.
 *
 * All return typed responses OR throw ApiError (from lib/api). The auth
 * context uses these directly — no PKCE/state-cookie ceremony needed
 * anymore because the auth flow is now email+password server-mediated.
 */

import { api } from "./api";

export interface TokensResponse {
  access_token: string;
  id_token: string;
  expires_in: number;
  token_type: "Bearer";
}

export interface CodeDelivery {
  destination?: string;   // masked email like "m***@c***.mx"
  medium?: string;         // "EMAIL" | "SMS"
}

export interface SignUpResponse {
  userSub: string;
  userConfirmed: boolean;
  codeDelivery: CodeDelivery | null;
}

export function signUp(input: {
  email: string;
  password: string;
  name?: string;
  locale?: string;
}): Promise<SignUpResponse> {
  return api<SignUpResponse>("/api/auth/signup", { method: "POST", json: input });
}

/**
 * Confirm signup with the 6-digit code.
 *
 * If `password` is passed, the endpoint will chain a Cognito InitiateAuth
 * call and return tokens (same shape as /api/auth/signin). If it isn't,
 * or if auto-signin fails server-side, the response is `{}` and the
 * caller should navigate to /login.
 */
export function confirmSignUp(input: { email: string; code: string; password?: string }): Promise<TokensResponse | Record<string, never>> {
  return api<TokensResponse | Record<string, never>>("/api/auth/confirm", { method: "POST", json: input });
}

/** Type guard: did /api/auth/confirm return tokens (auto-signin succeeded)? */
export function isTokensResponse(r: TokensResponse | Record<string, never>): r is TokensResponse {
  return typeof (r as TokensResponse).access_token === "string";
}

export function signIn(input: { email: string; password: string }): Promise<TokensResponse> {
  return api<TokensResponse>("/api/auth/signin", { method: "POST", json: input });
}

export function forgotPassword(input: { email: string }): Promise<{ codeDelivery: CodeDelivery | null }> {
  return api<{ codeDelivery: CodeDelivery | null }>("/api/auth/forgot-password", { method: "POST", json: input });
}

export function resetPassword(input: { email: string; code: string; newPassword: string }): Promise<Record<string, never>> {
  return api<Record<string, never>>("/api/auth/reset-password", { method: "POST", json: input });
}

export function resendCode(input: { email: string }): Promise<{ codeDelivery: CodeDelivery | null }> {
  return api<{ codeDelivery: CodeDelivery | null }>("/api/auth/resend-code", { method: "POST", json: input });
}
